import { create } from "zustand";
import type { Direction, Route } from "@/pages/home/types/directionsTypes";

interface MapRouteState {
  route: Route;
  direction: Direction | null;
  currentFloor: number;
  selectedSegmentId: number | null;
  focusFullRoute: boolean;
  isCameraAnimating: boolean;
  isRouteLoading: boolean;
  isFloorManuallySelected: boolean;
  // Режим пошаговой навигации
  isNavigationMode: boolean;
  navigationStepIndex: number;
}

interface MapRouteActions {
  setRoute: (route: Route | ((prev: Route) => Route)) => void;
  setDirection: (direction: Direction | null) => void;
  setCurrentFloor: (floor: number) => void;
  setSelectedSegmentId: (id: number | null) => void;
  setFocusFullRoute: (focus: boolean) => void;
  setIsCameraAnimating: (animating: boolean) => void;
  setIsRouteLoading: (loading: boolean) => void;
  setIsFloorManuallySelected: (selected: boolean) => void;
  // Комбинированные actions
  selectFloorManually: (floor: number) => void;
  showFullRoute: () => void;
  // Пошаговая навигация
  startNavigation: () => void;
  stopNavigation: () => void;
  navigateNext: () => void;
  navigatePrev: () => void;
}

const initialState: MapRouteState = {
  route: { start: "", end: "" },
  direction: null,
  currentFloor: 1,
  selectedSegmentId: null,
  focusFullRoute: false,
  isCameraAnimating: false,
  isRouteLoading: false,
  isFloorManuallySelected: false,
  isNavigationMode: false,
  navigationStepIndex: 0,
};

export const useMapRouteStore = create<MapRouteState & MapRouteActions>()(
  (set, get) => ({
    ...initialState,

    setRoute: (routeOrUpdater) => {
      set((state) => ({
        route:
          typeof routeOrUpdater === "function"
            ? routeOrUpdater(state.route)
            : routeOrUpdater,
      }));
    },

    // ВАЖНО: Сохраняем логику useEffect из RouteContext.tsx
    // Сброс isFloorManuallySelected при изменении direction
    setDirection: (direction) => {
      set({
        direction,
        isFloorManuallySelected: false,
      });
    },

    setCurrentFloor: (currentFloor) => set({ currentFloor }),

    // ВАЖНО: Сохраняем логику useEffect из RouteContext.tsx
    // Сброс isFloorManuallySelected при выборе сегмента (НЕ при null)
    setSelectedSegmentId: (selectedSegmentId) => {
      set((state) => ({
        selectedSegmentId,
        isFloorManuallySelected:
          selectedSegmentId !== null ? false : state.isFloorManuallySelected,
      }));
    },

    setFocusFullRoute: (focusFullRoute) => set({ focusFullRoute }),
    setIsCameraAnimating: (isCameraAnimating) => set({ isCameraAnimating }),
    setIsRouteLoading: (isRouteLoading) => set({ isRouteLoading }),
    setIsFloorManuallySelected: (isFloorManuallySelected) =>
      set({ isFloorManuallySelected }),

    // Комбинированный action для floorSelector
    selectFloorManually: (floor) => {
      const state = get();
      if (state.direction) {
        set({
          currentFloor: floor,
          isFloorManuallySelected: true,
          selectedSegmentId: null,
        });
      } else {
        set({ currentFloor: floor });
      }
    },

    // Комбинированный action для routeSummary
    showFullRoute: () => {
      set({
        selectedSegmentId: null,
        focusFullRoute: true,
        isFloorManuallySelected: false,
        isNavigationMode: false,
        navigationStepIndex: 0,
      });
    },

    // Пошаговая навигация
    startNavigation: () => {
      const segments = get().direction?.segmentationRoute.segments;
      if (!segments?.length) return;

      set({
        isNavigationMode: true,
        navigationStepIndex: 0,
      });
      get().setSelectedSegmentId(segments[0].id);
    },

    stopNavigation: () => {
      set({
        isNavigationMode: false,
        navigationStepIndex: 0,
        selectedSegmentId: null,
      });
    },

    navigateNext: () => {
      const { navigationStepIndex, direction } = get();
      const segments = direction?.segmentationRoute.segments;
      if (!segments) return;

      const nextIndex = navigationStepIndex + 1;
      if (nextIndex < segments.length) {
        set({ navigationStepIndex: nextIndex });
        get().setSelectedSegmentId(segments[nextIndex].id);
      }
    },

    navigatePrev: () => {
      const { navigationStepIndex, direction } = get();
      const segments = direction?.segmentationRoute.segments;
      if (!segments) return;

      const prevIndex = navigationStepIndex - 1;
      if (prevIndex >= 0) {
        set({ navigationStepIndex: prevIndex });
        get().setSelectedSegmentId(segments[prevIndex].id);
      }
    },
  }),
);
